import React, { useEffect, useState, useRef } from "react";
import styles from "./Resumen.module.scss";
import { Mail, Phone, Globe, Instagram, Linkedin, Facebook, Twitter, CircleCheck, MapPin, Sparkles, Users } from "lucide-react";
import MisSkills from "../MisSkills/MisSkills";
import RegisteredUserProfileLoader from "../../../RegisteredUsers/RegisteredUsersSkeleton/RegisteredUserProfileLoader";
import { useActionGuard } from "../../../../hooks/useActionGuard.ts";

const MIN_LOADING_TIME = 400;

const Resumen = ({ myPerson, userData, profileVisualizerData, perfilAjeno, setInfoReady, onOpenPostModal }) => {
  const { guard } = useActionGuard();
  const [isDataReady, setIsDataReady] = useState(false);
  const loadingStartTimeRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setIsDataReady(false);
    loadingStartTimeRef.current = Date.now();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const dataSource = myPerson ? userData : (profileVisualizerData || {});
    const isCorrectUser = myPerson ? true : (profileVisualizerData?.id === perfilAjeno || !perfilAjeno);
    const hasData = dataSource && (dataSource.username || dataSource.description !== undefined) && isCorrectUser;

    if (hasData) {
      const elapsedTime = Date.now() - loadingStartTimeRef.current;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);

      timeoutRef.current = setTimeout(() => {
        setIsDataReady(true);
        setInfoReady(true);
      }, remainingTime);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [myPerson, userData, profileVisualizerData, perfilAjeno, setInfoReady]);

  const {
    linkedin,
    instagram,
    facebook,
    twitter,
    phone,
    type,
    description,
    jobBoardZones,
    username,
    categoryConnect
  } = myPerson ? userData : (profileVisualizerData || {});

  const formatCategoryConnectText = () => {
    const categories = Array.isArray(categoryConnect) ? categoryConnect.map((c) => c?.name).filter(Boolean) : [];
    if (categories.length === 0) return null;
    const list = categories.length === 1
      ? categories[0]
      : categories.length === 2
        ? `${categories[0]} y ${categories[1]}`
        : `${categories.slice(0, -1).join(", ")} y ${categories[categories.length - 1]}`;
    const listPhrase = categories.length > 1 ? `áreas como ${list}` : list;
    if (myPerson) {
      return `Te interesa conectar con profesionales de ${listPhrase}.`;
    }
    if (type === "COMPANY") {
      return `La empresa está interesada en conectar con profesionales de ${listPhrase}.`;
    }
    return `Le interesa conectar con profesionales de ${listPhrase}.`;
  };

  const categoryConnectText = formatCategoryConnectText();


  return (
    <>
      {!isDataReady ? (
        <div className={styles.loadingContainer}>
          <RegisteredUserProfileLoader noBackground={true} />
        </div>
      ) : (
        <div className={styles.resumen}>

          <div className={styles.resumen_leftSide}>
            <div className={styles.resumen_section}>
              <h4 className={styles.resumen_section_title}>
                Contacto
              </h4>
              <div className={styles.resumen_section_info}>
                <div className={styles.resumen_section_info_item}>
                  <Mail size={20} className={styles.resumen_section_info_item_icon} />
                  <p className={styles.resumen_section_info_item_data}>{username}</p>
                </div>
                {phone && (
                  <div className={styles.resumen_section_info_item}>
                    <Phone size={20} className={styles.resumen_section_info_item_icon} />
                    <p className={styles.resumen_section_info_item_data}>{phone}</p>
                  </div>
                )}
                {instagram && (
                  <div className={styles.resumen_section_info_item}>
                    <Instagram size={20} className={styles.resumen_section_info_item_icon} />
                    <p className={styles.resumen_section_info_item_data}>{instagram}</p>
                  </div>
                )}
                {linkedin && (
                  <div className={styles.resumen_section_info_item}>
                    <Linkedin size={20} className={styles.resumen_section_info_item_icon} />
                    <p className={styles.resumen_section_info_item_data}>{linkedin}</p>
                  </div>
                )}
                {facebook && (
                  <div className={styles.resumen_section_info_item}>
                    <Facebook size={20} className={styles.resumen_section_info_item_icon} />
                    <p className={styles.resumen_section_info_item_data}>{facebook}</p>
                  </div>
                )}
                {twitter && (
                  <div className={styles.resumen_section_info_item}>
                    <Twitter size={20} className={styles.resumen_section_info_item_icon} />
                    <p className={styles.resumen_section_info_item_data}>{twitter}</p>
                  </div>
                )}
              </div>
            </div>
            {categoryConnectText && (
              <div className={styles.resumen_section}>
                <h4 className={styles.resumen_section_title}>
                  Conexiones
                </h4>
                <p className={styles.resumen_section_text}>{categoryConnectText}</p>
              </div>
            )}
          </div>
          <div className={styles.resumen_rightSide}>
            <div className={styles.resumen_section}>
              <div className={styles.resumen_section_header}>
                <h4 className={styles.resumen_section_title}>
                  Descripción {type === "COMPANY" ? "de la empresa" : "profesional"}
                </h4>
                {myPerson && onOpenPostModal && (
                  <button
                    type="button"
                    className={styles.generatePostButton}
                    onClick={guard("generate_post", onOpenPostModal)}
                    title="Generar texto para presentarte"
                  >
                    <Sparkles size={16} />
                    Generar post
                  </button>
                )}
              </div>
              <p className={styles.resumen_section_text}>
                {description === null || description === "" ? "No especificado" : description}
              </p>
            </div>
            <div className={styles.resumen_section}>
              <h4 className={styles.resumen_section_title}>
                Rubros
              </h4>
              <MisSkills perfilAjeno={perfilAjeno} />
            </div>
            <div className={styles.resumen_section}>
              <h4 className={styles.resumen_section_title}>
                Zona de trabajo
              </h4>
              <div className={styles.resumen_section_info}>
                {jobBoardZones && jobBoardZones.some(zone => zone?.allCountry) ? (
                  <div className={styles.resumen_section_info_allCountry}>
                    <Globe size={20} className={styles.resumen_section_info_item_icon} />
                    <div className={styles.resumen_section_info_allCountry_data}>
                      <p className={styles.resumen_section_info_allCountry_data_title}>Disponibilidad nacional</p>
                      <p className={styles.resumen_section_info_allCountry_data_text}>Servicios/productos disponibles en todo el país</p>
                    </div>
                    <CircleCheck size={20} className={styles.resumen_section_info_allCountry_icon} />
                  </div>
                ) : (
                  <div className={styles.resumen_section_info_zones}>
                    {jobBoardZones && jobBoardZones.map((zone, index) => (
                      <div key={index} className={styles.resumen_section_info_zones_item}>
                        <MapPin size={20} className={styles.resumen_section_info_item_icon} />
                        <div className={styles.resumen_section_info_item_data}>
                          <p className={styles.resumen_section_info_allCountry_data_title}>{zone?.jurisdiccion?.name}</p>
                          <p className={styles.resumen_section_info_item_allCountry_text}>{zone?.city?.split(',')[0]}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )

}

export default Resumen;